import {
  ArtifactStateMark,
  EvidenceLink,
  KnowledgeItem,
  NoteIcon,
  ProblemBox,
  SectionNav,
  Sheet,
  WorkflowItem,
} from "@don/design-system";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";

import type { SessionKnowledge, SessionKnowledgeView } from "../knowledge/source.js";
import {
  artifactCopy,
  isReading,
  railKindFrom,
  railKindLabel,
  readingOf,
  atText,
  stepAtText,
  transcriptRows,
  BEING_WRITTEN,
  type ArtifactCopy,
  type Reading,
  type ReadingPort,
  type RailEntry,
  type SegmentReading,
  type SummaryStep,
  type TranscriptRow,
} from "./reading.js";
import { timecode, type SessionGap } from "./timeline.js";

/**
 * The side column (issue 202; design-spec §3 "Side column", PRD module 9): on the sheet,
 * section links over one scroll, Summary (with its Observations), Knowledge, Workflows,
 * Transcript, Notes. Clicking a link scrolls its section to the top; scrolling moves the
 * current link (D29). The bottom padding (120) lets the last section reach the top.
 *
 * What is here says what the session was about; what plays is the well's. **It renders
 * beside the aged-out shape too**: the video goes after 30 days and the writing stays a year
 * (decisions §(a)); what goes with the video is seeking, so evidence links read as plain
 * times then.
 *
 * No legend, no accent footer and no label marking generated sections (D35); cyan for AI is
 * retired (D10). The only accent here is the evidence link, the one thing that invites a
 * click (D8).
 *
 * **Two modes** (issue 203, design-spec §3 "Hide-video mode"). `split`: one grid cell, the sheet
 * with the links on top. `reading`: two grid cells, the links stacked in the first
 * (`data-reading-nav`) and the sheet in the second, with `banner` above its scroll.
 */

/** The five sections, in scroll order (D19). */
const SECTIONS = [
  { id: "summary", label: "Summary" },
  { id: "knowledge", label: "Knowledge" },
  { id: "workflows", label: "Workflows" },
  { id: "transcript", label: "Transcript" },
  { id: "notes", label: "Notes" },
] as const;
type SectionId = (typeof SECTIONS)[number]["id"];

/** A section counts as current once its top is this close to the top of the scroll. */
const CURRENT_SLACK_PX = 24;

/**
 * The owner's two writes on a draft, each resolving once the source has recorded it (issue
 * 204). Absent when the source offers no write: then no item draws Discard or Keep.
 */
export interface KnowledgeWrites {
  keep(itemId: string): Promise<void>;
  discard(itemId: string): Promise<void>;
}

/** The catalog's copy for a reading read that did not answer (`session-reading-failed`). */
export const READING_FAILED_TITLE = "Couldn't load the summary, transcript and notes.";

export function ReadingColumn({
  reading,
  positionMs,
  onSeek,
  playing = false,
  gaps = [],
  person,
  knowledge = "none",
  workflows,
  knowledgeWrites,
  onRetryKnowledge,
  videoPlays = true,
  onRetry,
  mode = "split",
  banner,
}: {
  /** The port's value: a reading, `null` before one is read, `"failed"` when the read failed. */
  reading: ReadingPort;
  positionMs: number;
  /** `null` once the video has gone: there is nothing left to seek. */
  onSeek: ((positionMs: number) => void) | null;
  /** The picture is playing: the current transcript line is toned and kept in view (F1, issue 200). */
  playing?: boolean;
  /** The screen's attributed gaps, drawn as transcript rows in the one gap form (P5). */
  gaps?: readonly SessionGap[];
  /** Who the session belongs to: the bookends' speaker and the gaps' cause. */
  person?: string;
  knowledge?: SessionKnowledgeView;
  /**
   * What Workflows draws, where it differs from `knowledge`: `none` (`Coming soon`) when the
   * session source answers no workflows, as the derived route does (issue 229).
   */
  workflows?: SessionKnowledgeView;
  /** Keep and Discard on a draft; absent when the source offers no write (issue 204). */
  knowledgeWrites?: KnowledgeWrites;
  /** Ask the knowledge source again: the failed Knowledge box's `Try again`. */
  onRetryKnowledge?: () => void;
  /** Whether the well beside this still plays, which the reading-failed sentence states. */
  videoPlays?: boolean;
  /** Read the session again: the failed boxes' `Try again`. */
  onRetry?: () => void;
  /** `split` beside the well; `reading` for hide video and a deleted video (issue 203). */
  mode?: "split" | "reading";
  /** What stands at the top of the sheet, above the scroll: the deleted video's box (issue 203). */
  banner?: ReactNode;
}) {
  const built = reading === null || reading === "failed" ? reading : readingOf(reading);
  // Where the links go and what wraps the scroll: the frame of either mode.
  const frame: Frame = (nav, body) => {
    const sheet = (
      <Sheet
        data-reading-column=""
        style={{
          // Fills its cell of the session split (issue 200), aligned with the well's top.
          minWidth: 0,
          minHeight: 0,
          marginTop: "var(--space-2)",
          display: "flex",
          flexDirection: "column",
          padding: "var(--space-15) var(--space-35) 0",
        }}
      >
        {mode === "split" && nav}
        {banner !== undefined && <div style={{ flex: "none", padding: "var(--space-1) 0 var(--space-05)" }}>{banner}</div>}
        {body}
      </Sheet>
    );
    // One shape in both modes, the nav cell's slot empty in split: the sheet, and the scroll
    // inside it, stay the same nodes across a mode change, so the owner's place, the current
    // link and the focused evidence link survive Hide and Show (issue 203 rework).
    return (
      <>
        {mode === "reading" && (
          // Lined up with the first section's heading: the sheet's 16 margin, 12 padding and
          // the section's 20 top padding.
          <div data-reading-nav="" style={{ minWidth: 0, paddingTop: "var(--space-6)" }}>
            {nav}
          </div>
        )}
        {sheet}
      </>
    );
  };
  return built === null || built === "failed" || !isReading(built) ? (
    frame(
      null,
      built === null ? (
        <NotRead />
      ) : built === "failed" ? (
        <div data-reading-failed="" style={{ paddingTop: "var(--space-2)" }}>
          <ProblemBox onRetry={onRetry}>
            <b style={{ fontWeight: "var(--type-body-weight-current)" }}>{READING_FAILED_TITLE}</b>{" "}
            {videoPlays ? "The server didn't answer; the video still plays." : "The server didn't answer."}
          </ProblemBox>
        </div>
      ) : (
        <ReadingFault fault={built.fault} detail={built.detail} />
      ),
    )
  ) : (
    <Column
      reading={built}
      positionMs={positionMs}
      onSeek={onSeek}
      playing={playing}
      gaps={gaps}
      person={person}
      knowledge={knowledge}
      workflows={workflows ?? knowledge}
      knowledgeWrites={knowledgeWrites}
      onRetryKnowledge={onRetryKnowledge}
      onRetry={onRetry}
      frame={frame}
      stacked={mode === "reading"}
    />
  );
}

/** Places the section links and the scroll for the column's mode. */
type Frame = (nav: ReactNode, body: ReactNode) => ReactNode;

/** Not read yet, which is not a session with nothing in it: a pending ring and a label. */
function NotRead() {
  return (
    <div data-reading-absent="" role="status" style={{ paddingTop: "var(--space-2)" }}>
      <ArtifactStateMark state="pending">Loading the summary, transcript and notes</ArtifactStateMark>
    </div>
  );
}

/**
 * A reading `readingOf` refused, scoped to this column so the well beside it is untouched.
 * `detail` is for whoever fixes the producer (milliseconds, port field names), so it is on the
 * element and not in the sentence.
 */
function ReadingFault({ fault, detail }: { fault: string; detail: string }) {
  return (
    <div data-reading-fault={fault} data-reading-fault-detail={detail} style={{ paddingTop: "var(--space-2)" }}>
      <ProblemBox>
        <b style={{ fontWeight: "var(--type-body-weight-current)" }}>This writing doesn't describe this session.</b>{" "}
        None of it is shown. What was recorded is unaffected.
      </ProblemBox>
    </div>
  );
}

function Column({
  reading,
  positionMs,
  onSeek,
  playing,
  gaps,
  person,
  knowledge,
  workflows,
  knowledgeWrites,
  onRetryKnowledge,
  onRetry,
  frame,
  stacked,
}: {
  reading: Reading;
  positionMs: number;
  onSeek: ((positionMs: number) => void) | null;
  playing: boolean;
  gaps: readonly SessionGap[];
  person?: string;
  knowledge: SessionKnowledgeView;
  workflows: SessionKnowledgeView;
  knowledgeWrites?: KnowledgeWrites;
  onRetryKnowledge?: () => void;
  onRetry?: () => void;
  frame: Frame;
  stacked: boolean;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState<SectionId>("summary");

  // The current link follows the scroll (D29): the last section whose top has reached the
  // top of the scroll, or the last section once the scroll can go no further.
  const follow = useCallback(() => {
    const el = scroller.current;
    if (el === null) return;
    const top = el.getBoundingClientRect().top;
    let here: SectionId = SECTIONS[0].id;
    for (const node of Array.from(el.querySelectorAll<HTMLElement>("[data-section]"))) {
      if (node.getBoundingClientRect().top - top <= CURRENT_SLACK_PX) here = node.dataset.section as SectionId;
    }
    if (el.scrollTop > 0 && el.scrollTop + el.clientHeight >= el.scrollHeight - 1) here = SECTIONS[SECTIONS.length - 1].id;
    setCurrent(here);
  }, []);

  const jump = (id: string) => {
    const el = scroller.current;
    const target = el?.querySelector<HTMLElement>(`[data-section="${id}"]`);
    if (el == null || target == null) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollTo({ top: target.offsetTop, behavior: reduce ? "auto" : "smooth" });
  };

  // A mode change reflows the scroll at a new width (issue 203 rework). The node is kept (see
  // `frame`) and the browser's scroll anchoring holds the section at the top; the focused
  // evidence link that brought the video back is kept in view, and the current link is
  // measured again from where the scroll now is.
  const lastStacked = useRef(stacked);
  useLayoutEffect(() => {
    if (lastStacked.current === stacked) return;
    lastStacked.current = stacked;
    const el = scroller.current;
    const focused = document.activeElement;
    if (el !== null && focused instanceof HTMLElement && el.contains(focused)) focused.scrollIntoView({ block: "nearest" });
    follow();
  }, [stacked, follow]);

  const lineIndex = activeIndex(reading.transcript.map((line) => line.atMs), positionMs);

  // While the picture plays, the current line follows it into view (F1, issue 200); paused,
  // the column stays where the owner put it.
  useEffect(() => {
    if (!playing) return;
    scroller.current?.querySelector("[data-transcript-active]")?.scrollIntoView({ block: "nearest" });
  }, [playing, lineIndex]);

  const seek = (atMs: number) => (onSeek === null ? undefined : () => onSeek(atMs));

  return frame(
    <SectionNav
      label="Sections"
      items={[...SECTIONS]}
      current={current}
      onSelect={jump}
      orientation={stacked ? "column" : "row"}
    />,
      <div
        ref={scroller}
        data-reading-scroll=""
        onScroll={follow}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          position: "relative",
          // 20 each side so a toned row can bleed, and 120 below so the last section can
          // reach the top (§3).
          padding: "0 var(--space-25) calc(var(--space-10) + var(--space-5))",
          margin: "0 calc(var(--space-25) * -1)",
        }}
      >
        <Section id="summary" title="Summary" first>
          <SummaryBody reading={reading} seek={seek} off={onSeek === null} onRetry={onRetry} />
        </Section>
        <Section id="knowledge" title="Knowledge">
          <KnowledgeBody
            knowledge={knowledge}
            part="items"
            seek={seek}
            off={onSeek === null}
            writes={knowledgeWrites}
            onRetry={onRetryKnowledge}
          />
        </Section>
        <Section id="workflows" title="Workflows">
          <KnowledgeBody knowledge={workflows} part="workflows" seek={seek} off={onSeek === null} />
        </Section>
        <Section id="transcript" title="Transcript">
          <TranscriptBody
            reading={reading}
            rows={transcriptRows(reading, gaps, person)}
            lineIndex={lineIndex}
            playing={playing}
            seek={seek}
            off={onSeek === null}
            onRetry={onRetry}
          />
        </Section>
        <Section id="notes" title="Notes">
          {reading.notes.length === 0 ? (
            <Quiet>No notes.</Quiet>
          ) : (
            reading.notes.map((note, index) => (
              <Row key={`note-${note.atMs}-${index}`} entry={note} at={<Evidence atMs={note.atMs} seek={seek} off={onSeek === null} />}>
                {note.text}
              </Row>
            ))
          )}
        </Section>
      </div>,
  );
}

type Seek = (atMs: number) => (() => void) | undefined;

function Section({ id, title, first = false, children }: { id: SectionId; title: string; first?: boolean; children: ReactNode }) {
  return (
    <section
      data-section={id}
      aria-labelledby={`section-${id}`}
      style={{
        padding: "var(--space-25) 0 calc(var(--space-4) + var(--space-05))",
        borderTop: first ? undefined : "var(--border-hairline) solid var(--line)",
      }}
    >
      <h2
        id={`section-${id}`}
        style={{
          margin: "0 0 var(--space-15)",
          fontSize: "var(--type-title-size)",
          lineHeight: "var(--type-title-line)",
          fontWeight: "var(--type-title-weight)",
          letterSpacing: "var(--type-title-tracking)",
          color: "var(--ink-1)",
        }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

/**
 * An artifact that is not present, in one of three shapes (P5): pending ring and `Being
 * written.`; barred ring and why, in ink-2; the red-edged ProblemBox with `Try again`, the
 * only problem styling.
 */
function StateBody({ artifact, copy, onRetry }: { artifact: "summary" | "transcript"; copy: ArtifactCopy; onRetry?: () => void }) {
  const hooks = {
    "data-artifact": artifact,
    "data-artifact-state": copy.state === "absent" ? "absent_by_design" : copy.state,
    "data-absent-reason": copy.state === "absent" && copy.reason !== null ? copy.reason : undefined,
  };
  if (copy.state === "failed") {
    return (
      <div {...hooks}>
        <ProblemBox onRetry={onRetry}>
          {copy.text} {copy.next}
        </ProblemBox>
      </div>
    );
  }
  return (
    <p {...hooks} style={{ margin: 0 }}>
      <ArtifactStateMark state={copy.state}>{copy.text}</ArtifactStateMark>
    </p>
  );
}

/**
 * A present Summary's document: the paragraph in the prose type, then the steps in order, each
 * beside its place as plain text. Shared by a session's Summary and each of a recording's.
 */
function SummaryDocument({ paragraph, steps }: { paragraph: string | null; steps: readonly SummaryStep[] }) {
  return (
    <div data-artifact="summary" data-artifact-state="present">
      {paragraph !== null && (
        <p
          data-summary-paragraph=""
          style={{
            margin: 0,
            maxWidth: "var(--reading-measure)",
            fontSize: "var(--type-prose-size)",
            lineHeight: "var(--type-prose-line)",
            fontWeight: "var(--type-prose-weight)",
            letterSpacing: "var(--type-prose-tracking)",
            color: "var(--ink-1)",
          }}
        >
          {paragraph}
        </p>
      )}
      {steps.length > 0 && (
        <ol
          data-summary-steps=""
          aria-label="Steps"
          style={{ listStyle: "none", margin: paragraph === null ? 0 : "var(--space-15) 0 0", padding: 0 }}
        >
          {steps.map((step) => (
            <li
              key={step.id}
              data-summary-step={step.id}
              data-step-at={"bookend" in step.at ? step.at.bookend : String(step.at.atMs)}
              style={rowGrid(false)}
            >
              <span style={WHEN} data-step-time="">
                {stepAtText(step)}
              </span>
              <span style={{ minWidth: 0 }}>{step.text}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/** A segment's place on its recording's timeline, as its Summary is headed: `0:00 – 30:12`. */
export function segmentRange(segment: SegmentReading): string {
  return `${timecode(segment.startMs)} – ${timecode(segment.endMs)}`;
}

/**
 * A recording's Summaries (issue 321): one per segment, in ordinal order, each under its time
 * range on the recording's timeline. A segment whose Summary is not present says so in the one
 * state form a session's does (`artifactCopy`), so one pending segment does not hide the others.
 */
function RecordingSummaries({ reading, onRetry }: { reading: Reading; onRetry?: () => void }) {
  return (
    <ol data-recording-summaries="" aria-label="Summaries" style={{ listStyle: "none", margin: 0, padding: 0 }}>
      {reading.segments.map((segment, index) => {
        const copy = artifactCopy("Summary", {
          ...reading,
          summaryState: segment.summaryState,
          summaryAbsentReason: segment.summaryAbsentReason,
        });
        const paragraph = (segment.paragraph ?? "").trim().length > 0 ? segment.paragraph : null;
        return (
          <li
            key={segment.sessionId}
            data-recording-summary={segment.ordinal}
            data-summary-state={segment.summaryState}
            style={{ marginTop: index === 0 ? 0 : "var(--space-3)" }}
          >
            <h3
              data-summary-range=""
              style={{
                margin: "0 0 var(--space-05)",
                fontSize: "var(--type-small-size)",
                lineHeight: "var(--type-small-line)",
                fontWeight: "var(--type-body-weight-current)",
                color: "var(--ink-2)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {segmentRange(segment)}
            </h3>
            {copy !== null ? (
              <StateBody artifact="summary" copy={copy} onRetry={onRetry} />
            ) : paragraph === null && segment.steps.length === 0 ? (
              <div data-artifact="summary" data-artifact-state="present" data-summary-empty="">
                <Quiet>{SUMMARY_EMPTY}</Quiet>
              </div>
            ) : (
              <SummaryDocument paragraph={paragraph} steps={segment.steps} />
            )}
          </li>
        );
      })}
    </ol>
  );
}

/** A present Summary with nothing in it to draw: one quiet line, never a bare heading (P5). */
export const SUMMARY_EMPTY = "This summary has no text.";

/**
 * The Summary. A present Summary with a document (issue 228) is its paragraph, in the prose
 * type, then its steps in order, each beside its place as plain text: a timecode or `Before` /
 * `After`, which does not seek, because a step's time is a model's estimate. Every word is
 * model output and goes in as a text node, never as markup.
 *
 * A present Summary from a source with no document (fixtures only: the route always sends one
 * under `present`) draws its voiceovers, and with none, one quiet line. Never the bookends:
 * since issue 228 those hold the Transcript's lines, and drawing them here would show the
 * person's own words as the Summary's. Then the Observations, which are not the Summary's to withhold: a separate
 * artifact with its own provenance (`CONTEXT.md`), drawn in every Summary state, and not at
 * all when there are none.
 */
function SummaryBody({ reading, seek, off, onRetry }: { reading: Reading; seek: Seek; off: boolean; onRetry?: () => void }) {
  if (reading.segments.length > 0) return <RecordingSummaries reading={reading} onRetry={onRetry} />;
  const copy = artifactCopy("Summary", reading);
  const document = reading.paragraph !== null || reading.steps.length > 0;
  const spoken = reading.rail.entries.filter((entry) => entry.kind === "voiceover");
  const paragraph = (reading.paragraph ?? "").trim().length > 0 ? reading.paragraph : null;
  const empty = paragraph === null && reading.steps.length === 0 && (document || spoken.length === 0);
  return (
    <>
      {copy !== null ? (
        <StateBody artifact="summary" copy={copy} onRetry={onRetry} />
      ) : empty ? (
        <div data-artifact="summary" data-artifact-state="present" data-summary-empty="">
          <Quiet>{SUMMARY_EMPTY}</Quiet>
        </div>
      ) : document ? (
        <SummaryDocument paragraph={paragraph} steps={reading.steps} />
      ) : (
        <div data-artifact="summary" data-artifact-state="present">
          {spoken.map((entry, index) => (
            <Row key={`voiceover-${entry.atMs}-${index}`} entry={entry} at={<Evidence atMs={entry.atMs} seek={seek} off={off} />}>
              {entry.text}
            </Row>
          ))}
        </div>
      )}
      {reading.observations.length > 0 && (
        <div data-observations="" style={{ marginTop: "var(--space-3)" }}>
          <h3
            style={{
              margin: "0 0 var(--space-05)",
              fontSize: "var(--type-body-size)",
              lineHeight: "var(--type-body-line)",
              fontWeight: "var(--type-body-weight-current)",
              color: "var(--ink-1)",
            }}
          >
            Observations
          </h3>
          {reading.observations.map((entry, index) => (
            <Row
              key={`observation-${entry.atMs}-${index}`}
              entry={entry}
              at={<Evidence atMs={entry.atMs} seek={seek} off={off} />}
              who={<ObservationWho title={entry.title} />}
              italic
            >
              {entry.text}
            </Row>
          ))}
        </div>
      )}
    </>
  );
}

function TranscriptBody({
  reading,
  rows,
  lineIndex,
  playing,
  seek,
  off,
  onRetry,
}: {
  reading: Reading;
  rows: readonly TranscriptRow[];
  lineIndex: number;
  playing: boolean;
  seek: Seek;
  off: boolean;
  onRetry?: () => void;
}) {
  const copy = artifactCopy("Transcript", reading);
  if (copy !== null) return <StateBody artifact="transcript" copy={copy} onRetry={onRetry} />;
  let line = -1;
  return (
    <div data-artifact="transcript" data-artifact-state="present">
      {rows.map((row, index) => {
        switch (row.kind) {
          case "bookend":
            return (
              <Row key={`bookend-${row.which}`} kind="bookend" at={<When>{row.which === "intro" ? "Before" : "After"}</When>} who={row.speaker ?? undefined}>
                {row.text}
              </Row>
            );
          case "speech": {
            line += 1;
            const active = line === lineIndex;
            return (
              <Row
                key={`speech-${row.atMs}-${index}`}
                at={<Evidence atMs={row.atMs} seek={seek} off={off} />}
                who={<span data-transcript-speaker="">{row.speaker}</span>}
                line={row.atMs}
                active={active}
                toned={active && playing}
              >
                {row.text}
              </Row>
            );
          }
          case "note":
            return (
              <Row
                key={`note-${row.atMs}-${index}`}
                kind="note"
                at={<Evidence atMs={row.atMs} seek={seek} off={off} />}
                who={
                  <>
                    <NoteIcon />
                    {railKindLabel("note")}
                  </>
                }
              >
                {row.text}
              </Row>
            );
          case "observation":
            return (
              <Row
                key={`observation-${row.atMs}-${index}`}
                kind="observation"
                at={<Evidence atMs={row.atMs} seek={seek} off={off} />}
                who={<ObservationWho title={row.title} />}
                italic
              >
                {row.text}
              </Row>
            );
          case "gap":
            return (
              <div key={`gap-${row.atMs}-${index}`} data-transcript-gap="" style={rowGrid(false)}>
                <span data-elapsed="" style={WHEN}>
                  {timecode(row.atMs)}
                </span>
                <span
                  style={{
                    display: "flex", alignItems: "center", gap: "var(--space-15)",
                    fontSize: "var(--type-small-size)", color: "var(--ink-2)",
                  }}
                >
                  <i
                    aria-hidden="true"
                    style={{
                      display: "block", flex: "none", width: "var(--space-5)", height: "10px",
                      borderRadius: "var(--radius-bar)",
                      boxShadow: "inset 0 0 0 var(--border-hairline) var(--gap-hatch)",
                      background: "repeating-linear-gradient(135deg, var(--gap-hatch) 0 2px, transparent 2px 5px)",
                    }}
                  />
                  {row.label}
                </span>
              </div>
            );
        }
      })}
    </div>
  );
}

/**
 * Knowledge and Workflows (decision 38, issue 204): `Coming soon` with no source, nothing while
 * the source has not answered, `Being written.` when it answers pending, a failed read as the
 * ProblemBox (its one `Try again` in Knowledge, so one read has one retry), a derivation that
 * did not finish as the failed mark, an absent Summary as the absent mark (issue 229), and
 * otherwise the items (`KnowledgeItems`, evidence as text) and workflows (`WorkflowItem`, around
 * the same `Evidence` link Summary uses, D8).
 */
function KnowledgeBody({
  knowledge,
  part,
  seek,
  off,
  writes,
  onRetry,
}: {
  knowledge: SessionKnowledgeView;
  part: "items" | "workflows";
  seek: Seek;
  off: boolean;
  writes?: KnowledgeWrites;
  onRetry?: () => void;
}) {
  if (knowledge === "none") return <Quiet data-coming-soon="">Coming soon</Quiet>;
  if (knowledge === "unanswered") return null;
  if (knowledge === "failed") {
    return (
      <div data-knowledge-state="failed">
        <ProblemBox onRetry={part === "items" ? onRetry : undefined}>
          <b style={{ fontWeight: "var(--type-body-weight-current)" }}>{part === "items" ? KNOWLEDGE_FAILED : WORKFLOWS_FAILED}</b>{" "}
          The server didn't answer.
        </ProblemBox>
      </div>
    );
  }
  if (knowledge.state === "pending") {
    return (
      <p data-knowledge-state="pending" style={{ margin: 0 }}>
        <ArtifactStateMark state="pending">{BEING_WRITTEN}</ArtifactStateMark>
      </p>
    );
  }
  // The derivation did not finish (issue 229): the failed mark, and no `Try again`, because
  // asking again reads the same failure. Distinct from a read that failed (the ProblemBox above).
  if (knowledge.state === "failed") {
    return (
      <p data-knowledge-state="not-finished" style={{ margin: 0 }}>
        <ArtifactStateMark state="failed">{part === "items" ? KNOWLEDGE_NOT_FINISHED : WORKFLOWS_NOT_FINISHED}</ArtifactStateMark>
      </p>
    );
  }
  // Knowledge comes out of the Summary, so with no Summary there is none to show, which is not
  // `None found`: nothing looked.
  if (knowledge.state === "absent") {
    return (
      <p data-knowledge-state="absent" style={{ margin: 0 }}>
        <ArtifactStateMark state="absent">{part === "items" ? KNOWLEDGE_ABSENT : WORKFLOWS_ABSENT}</ArtifactStateMark>
      </p>
    );
  }
  if (part === "items") return <KnowledgeItems items={knowledge.items} writes={writes} />;
  if (knowledge.workflows.length === 0) return <Quiet data-knowledge-state="none">{NONE_FOUND}</Quiet>;
  return (
    <div data-knowledge-state="answered">
      {knowledge.workflows.map((workflow, index) => (
        <WorkflowItem
          key={workflow.id}
          data-workflow={workflow.id}
          first={index === 0}
          name={workflow.name}
          goal={workflow.goal}
          steps={workflow.steps.map((step) => ({
            text: step.text,
            app: step.app,
            evidence: step.atMs === null ? undefined : <Evidence atMs={step.atMs} seek={seek} off={off} />,
          }))}
        />
      ))}
    </div>
  );
}

/** A section the source answered with nothing (E's `silent` copy for Workflows). */
export const NONE_FOUND = "None found in this session.";
export const KNOWLEDGE_FAILED = "Couldn't load the knowledge.";
export const WORKFLOWS_FAILED = "Couldn't load the workflows.";
/** A derivation that did not finish (issue 229), as `The summary didn't finish.` reads. */
export const KNOWLEDGE_NOT_FINISHED = "The knowledge didn't finish.";
export const WORKFLOWS_NOT_FINISHED = "The workflows didn't finish.";
/** The Summary is absent by design, and the knowledge is drawn out of it (issue 229). */
export const KNOWLEDGE_ABSENT = "No knowledge: this session has no summary.";
export const WORKFLOWS_ABSENT = "No workflows: this session has no summary.";
/** Under a draft whose Keep or Discard the source did not record. */
export const WRITE_FAILED = "Not saved. The server didn't answer.";

type KnowledgeItemList = Extract<SessionKnowledge, { state: "answered" }>["items"];

const toggled = (set: ReadonlySet<string>, id: string, on: boolean): ReadonlySet<string> => {
  const next = new Set(set);
  if (on) next.add(id);
  else next.delete(id);
  return next;
};

/**
 * The session's items. A write in flight stands both actions down; a write the source refused
 * leaves the item a draft with one quiet line under it. The item itself changes (kept, or gone)
 * only when the answer does, which `App` does after the source resolves.
 *
 * Each item's evidence is its place as plain text (`Before`, `After` or a timecode), which does
 * not seek, for the reason a Summary step's does not: it is a model's estimate (issue 229). The
 * status label is drawn only beside Keep and Discard: from a source with no write every item is
 * a draft and there is nothing to decide, so the label is not rendered.
 */
function KnowledgeItems({ items, writes }: { items: KnowledgeItemList; writes?: KnowledgeWrites }) {
  const [working, setWorking] = useState<ReadonlySet<string>>(new Set());
  const [unsaved, setUnsaved] = useState<ReadonlySet<string>>(new Set());
  if (items.length === 0) return <Quiet data-knowledge-state="none">{NONE_FOUND}</Quiet>;
  const run = (id: string, write: (itemId: string) => Promise<void>) => () => {
    setWorking((current) => toggled(current, id, true));
    setUnsaved((current) => toggled(current, id, false));
    write(id).then(
      () => setWorking((current) => toggled(current, id, false)),
      () => {
        setWorking((current) => toggled(current, id, false));
        setUnsaved((current) => toggled(current, id, true));
      },
    );
  };
  return (
    <div data-knowledge-state="answered">
      {items.map((item, index) => (
        <KnowledgeItem
          key={item.id}
          data-knowledge-item={item.id}
          first={index === 0}
          kind={item.kind}
          statement={item.statement}
          status={writes === undefined ? undefined : item.status}
          evidence={
            item.evidence.length === 0 ? undefined : (
              <>
                {item.evidence.map((at, index) => (
                  <span
                    key={`${atText(at)}-${index}`}
                    data-knowledge-evidence={"bookend" in at ? at.bookend : String(at.atMs)}
                    style={WHEN}
                  >
                    {atText(at)}
                  </span>
                ))}
              </>
            )
          }
          onKeep={writes === undefined ? undefined : run(item.id, writes.keep)}
          onDiscard={writes === undefined ? undefined : run(item.id, writes.discard)}
          working={working.has(item.id)}
          note={unsaved.has(item.id) ? WRITE_FAILED : undefined}
        />
      ))}
    </div>
  );
}

/** The evidence link for a moment: `▶ 0:14`, seeking the player; a plain time once the video is gone. */
function Evidence({ atMs, seek, off }: { atMs: number; seek: Seek; off: boolean }) {
  return <EvidenceLink time={timecode(atMs)} onSeek={seek(atMs)} off={off} data-elapsed="" data-evidence-at={String(atMs)} />;
}

function ObservationWho({ title }: { title: string | null }) {
  return <>{title === null ? railKindLabel("observation") : `${railKindLabel("observation")} · ${title}`}</>;
}

const WHEN: CSSProperties = {
  fontSize: "var(--type-small-size)",
  lineHeight: "var(--type-small-line)",
  color: "var(--ink-2)",
  paddingTop: "var(--space-025)",
  fontVariantNumeric: "tabular-nums",
};

/** `Before` and `After`, where every other row says a timecode: a bookend has none. */
function When({ children }: { children: ReactNode }) {
  return <span style={WHEN}>{children}</span>;
}

/** Time column 76, then the row (§4 "Transcript row"). */
function rowGrid(toned: boolean): CSSProperties {
  return {
    display: "grid",
    gridTemplateColumns: "calc(var(--space-9) + var(--space-05)) minmax(0, 1fr)",
    columnGap: "var(--space-15)",
    padding: "var(--space-075) var(--space-1)",
    margin: "0 calc(var(--space-1) * -1)",
    borderRadius: "var(--radius-control)",
    // Toned only while playing: playing and paused differ (F1, variant E `.ve-tr.now`).
    background: toned ? "var(--tone)" : undefined,
    fontSize: "var(--type-body-size)",
    lineHeight: "1.55",
    color: "var(--ink-1)",
  };
}

function Row({
  entry,
  kind,
  at,
  who,
  italic = false,
  line,
  active = false,
  toned = false,
  children,
}: {
  entry?: RailEntry;
  kind?: "bookend" | "note" | "observation";
  at: ReactNode;
  who?: ReactNode;
  italic?: boolean;
  /** A transcript line's position, written as `data-transcript-line`. */
  line?: number;
  active?: boolean;
  toned?: boolean;
  children: ReactNode;
}) {
  const railKind = entry?.kind ?? kind;
  return (
    <div
      data-rail-kind={railKind}
      data-rail-from={railKind === undefined ? undefined : railKindFrom(railKind)}
      data-transcript-line={line}
      data-transcript-active={active ? "" : undefined}
      style={rowGrid(toned)}
    >
      <span>{at}</span>
      <div style={{ minWidth: 0 }}>
        {who !== undefined && (
          <div
            data-row-who=""
            style={{
              display: "flex", alignItems: "center", gap: "var(--space-075)",
              fontSize: "var(--type-small-size)", lineHeight: "var(--type-small-line)",
              fontWeight: "var(--type-small-weight-strong)", color: "var(--ink-2)",
            }}
          >
            {who}
          </div>
        )}
        <div style={italic ? { fontStyle: "italic", color: "var(--ink-2)" } : undefined}>{children}</div>
      </div>
    </div>
  );
}

function Quiet({ children, ...hooks }: { children: ReactNode; [data: `data-${string}`]: string | undefined }) {
  return (
    <p {...hooks} style={{ margin: 0, color: "var(--ink-2)" }}>
      {children}
    </p>
  );
}

/** The row the playhead is in: the last one that has happened, and none before it starts. */
function activeIndex(times: readonly number[], positionMs: number): number {
  let index = -1;
  for (let i = 0; i < times.length; i += 1) {
    if (times[i]! <= positionMs) index = i;
  }
  return index;
}
